import React from "react";
import { Link } from "react-router-dom";
import { Globe, Award, DollarSign, BookOpen, ArrowRight, CheckCircle2 } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";

const FUNDING_MODES = [
  {
    icon: Award,
    title: "Fully Funded Options",
    tag: "100% Covered",
    desc: "Covers full tuition fees, monthly living stipend, health insurance, airfare, and research grants. Ideal for high-achieving applicants.",
    highlights: ["Government Grants (GKS, MEXT, CSC, Chevening)", "University President/Excellence Fellowships", "100% Tuition Waiver + Monthly Allowance"],
  },
  {
    icon: DollarSign,
    title: "Partial Funding Options",
    tag: "25% – 75% Waiver",
    desc: "Covers partial tuition fees or merit-based tuition discounts. Perfect for applicants seeking cost-effective international degrees.",
    highlights: ["Merit-based Dean's Scholarships", "Regional / Country-specific Fee Discounts", "Tuition Offset + On-Campus Work Rights"],
  },
  {
    icon: BookOpen,
    title: "Self-Funded & Hybrid Pathways",
    tag: "Flexible Budget",
    desc: "Flexible study options at affordable public universities with low tuition fees, payment plans, and part-time work permissions.",
    highlights: ["Low/Zero Tuition European Public Universities", "Installment-based Tuition Plans", "Part-Time Work & Post-Study Work Visas"],
  },
];

const DESTINATIONS = [
  { country: "Asia", flag: "🌏", options: "Top Universities and Regional Scholarships" },
  { country: "Europe", flag: "🇪🇺", options: "Erasmus Mundus, DAAD, and European Excellence Awards" },
  { country: "Australia", flag: "🇦🇺", options: "Australia Awards and Vice-Chancellor Scholarships" },
  { country: "New Zealand", flag: "🇳🇿", options: "Manaaki New Zealand Scholarships and University Grants" },
  { country: "United Kingdom", flag: "🇬🇧", options: "Chevening, GREAT, Commonwealth & University Merit Awards" },
  { country: "Ireland", flag: "🇮🇪", options: "Government of Ireland International Education Scholarships" },
  { country: "Canada", flag: "🇨🇦", options: "Vanier CGS, Trudeau Foundation, and University Fellowships" },
  { country: "USA", flag: "🇺🇸", options: "Fulbright, Graduate Assistantships, Institutional Aid" },
];

export default function StudyAbroad() {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-ribbon overflow-hidden" data-testid="studyabroad-hero">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-20 lg:pt-28 lg:pb-28 relative">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white text-brand-ink text-[11px] uppercase tracking-[0.2em] font-semibold border border-brand-line">
              <Globe size={13} className="text-brand" /> Study Abroad Hub
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-5 leading-[1.02] text-brand-ink">
              Study Abroad with <span className="text-brand">Partial, Self-Funded & Fully Funded</span> Options.
            </h1>
            <p className="mt-5 text-brand-muted text-lg leading-relaxed">
              Explore tailored university pathways across top destinations. Whether you are aiming for 100% government fellowships, merit fee waivers, or affordable self-funded programs, Veritas Sphere guides your entire journey.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/scholarships" className="btn-brand inline-flex items-center gap-2" data-testid="studyabroad-explore-cta">
                Explore Scholarships <ArrowRight size={18} strokeWidth={1.7} />
              </Link>
              <Link to="/contact" className="btn-outline-brand inline-flex items-center gap-2" data-testid="studyabroad-contact-cta">
                Contact Advisory
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Funding Pathways */}
      <section className="section bg-brand-cream" data-testid="studyabroad-pathways">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle
            eyebrow="Funding Options"
            title="Choose the financial pathway that fits your goal."
            subtitle="We match your academic profile, budget, and destination preferences with verified university funding options."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {FUNDING_MODES.map((m, i) => (
              <div key={i} className="card-soft p-8 flex flex-col justify-between" data-testid={`funding-card-${i}`}>
                <div>
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand grid place-items-center">
                      <m.icon size={22} strokeWidth={1.6} />
                    </div>
                    <span className="px-3 py-1 rounded-full bg-brand-ink text-white text-[11px] font-semibold">
                      {m.tag}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-brand-ink mt-6">{m.title}</h3>
                  <p className="text-brand-muted text-sm leading-relaxed mt-3">{m.desc}</p>
                  <ul className="mt-6 space-y-2.5">
                    {m.highlights.map((h, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-brand-ink/90">
                        <CheckCircle2 size={14} className="text-brand shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-8 pt-6 border-t border-brand-line">
                  <Link to="/scholarships" className="text-brand text-xs font-semibold hover:underline inline-flex items-center gap-1">
                    Find relevant programs →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Destinations */}
      <section className="section bg-white" data-testid="studyabroad-destinations">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle
            eyebrow="Global Destinations"
            title="Top study-abroad countries & university systems."
            subtitle="Direct admissions and scholarship support across leading international hubs."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, i) => (
              <div key={i} className="rounded-2xl border border-brand-line p-6 hover:shadow-lg transition-all" data-testid={`dest-card-${i}`}>
                <div className="text-3xl mb-3">{d.flag}</div>
                <div className="font-display text-lg font-bold text-brand-ink">{d.country}</div>
                <div className="text-xs text-brand-muted mt-2 leading-relaxed">{d.options}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 btn-brand text-sm px-6 py-3.5">
              Enquire for further details <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
